/**
 * AdminUsers Component
 * Page for managing all users with full CRUD operations
 */

import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminUsers extends Component {
    constructor(props) {
        super(props);
        this.state = {
            users: [],
            loading: true,
            error: null,
            search: '',
            page: 1,
            limit: 20,
            totalPages: 1,
            totalCount: 0,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            // Modal state
            showModal: false,
            modalMode: 'view', // 'view' or 'edit'
            selectedUser: null,
            editForm: {},
            saving: false
        };
        this.searchTimeout = null;
    }

    componentDidMount() {
        this.fetchUsers();
    }

    fetchUsers = async () => {
        const { page, limit, search, sortBy, sortOrder } = this.state;
        this.setState({ loading: true });

        try {
            const response = await adminService.getUsers({
                page,
                limit,
                search,
                sortBy,
                sortOrder
            });

            if (response.statusCode === 1) {
                this.setState({
                    users: response.responseData.users || [],
                    totalPages: response.responseData.pagination?.pages || 1,
                    totalCount: response.responseData.pagination?.total || 0,
                    loading: false
                });
            } else {
                this.setState({
                    error: 'Failed to load users',
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            this.setState({
                error: 'Failed to load users',
                loading: false
            });
        }
    }

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 1 });

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            // Only search with 2+ characters, or fetch all when empty
            if (search.trim().length >= 2 || search.trim().length === 0) {
                this.fetchUsers();
            }
        }, 300);
    }

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchUsers);
    }

    handleDelete = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteUser(userId);
            if (response.statusCode === 1) {
                this.fetchUsers();
                this.closeModal();
            } else {
                alert('Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Failed to delete user');
        }
    }

    // Modal handlers
    openViewModal = (user) => {
        this.setState({
            showModal: true,
            modalMode: 'view',
            selectedUser: user,
            editForm: { ...user }
        });
    }

    openEditModal = (user) => {
        this.setState({
            showModal: true,
            modalMode: 'edit',
            selectedUser: user,
            editForm: {
                name: user.name || '',
                email: user.email || '',
                phoneNumber: user.phoneNumber || '',
                isAdmin: user.isAdmin || false,
                isActive: user.isActive !== false
            }
        });
    }

    closeModal = () => {
        this.setState({
            showModal: false,
            modalMode: 'view',
            selectedUser: null,
            editForm: {},
            saving: false
        });
    }

    handleEditFormChange = (field, value) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                [field]: value
            }
        }));
    }

    handleSaveUser = async () => {
        const { selectedUser, editForm } = this.state;
        this.setState({ saving: true });

        try {
            const response = await adminService.updateUser(selectedUser._id, editForm);
            if (response.statusCode === 1) {
                this.fetchUsers();
                this.closeModal();
            } else {
                alert('Failed to update user');
                this.setState({ saving: false });
            }
        } catch (error) {
            console.error('Error updating user:', error);
            alert('Failed to update user');
            this.setState({ saving: false });
        }
    }

    getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    renderModal() {
        const { showModal, modalMode, selectedUser, editForm, saving } = this.state;

        if (!showModal || !selectedUser) return null;

        return (
            <div className="admin-modal-overlay" onClick={this.closeModal}>
                <div className="admin-modal" onClick={e => e.stopPropagation()}>
                    <div className="admin-modal-header">
                        <h2>{modalMode === 'edit' ? 'Edit User' : 'User Details'}</h2>
                        <button className="admin-modal-close" onClick={this.closeModal}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="admin-modal-body">
                        {modalMode === 'view' ? (
                            // View Mode
                            <div className="admin-detail-grid">
                                <div className="admin-detail-section">
                                    <div className="admin-detail-avatar-large">
                                        {selectedUser.avatar ? (
                                            <img src={selectedUser.avatar} alt={selectedUser.name} />
                                        ) : (
                                            <div className="admin-avatar-placeholder-large">
                                                {this.getInitials(selectedUser.name)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="admin-detail-row">
                                    <label>Name</label>
                                    <span>{selectedUser.name || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Email</label>
                                    <span>{selectedUser.email || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Phone</label>
                                    <span>{selectedUser.phoneNumber || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Status</label>
                                    <span className={`admin-badge ${selectedUser.isActive !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                        {selectedUser.isActive !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Admin Access</label>
                                    <span className={`admin-badge ${selectedUser.isAdmin ? 'admin-badge-info' : 'admin-badge-secondary'}`}>
                                        {selectedUser.isAdmin ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Joined</label>
                                    <span>{this.formatDateTime(selectedUser.createdTime || selectedUser.createdAt)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Last Updated</label>
                                    <span>{this.formatDateTime(selectedUser.updatedTime || selectedUser.updatedAt)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>User ID</label>
                                    <span className="admin-id-text">{selectedUser._id}</span>
                                </div>
                            </div>
                        ) : (
                            // Edit Mode
                            <div className="admin-form">
                                <div className="admin-form-group">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        value={editForm.name || ''}
                                        onChange={(e) => this.handleEditFormChange('name', e.target.value)}
                                        placeholder="Enter name"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={editForm.email || ''}
                                        onChange={(e) => this.handleEditFormChange('email', e.target.value)}
                                        placeholder="Enter email"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="tel"
                                        value={editForm.phoneNumber || ''}
                                        onChange={(e) => this.handleEditFormChange('phoneNumber', e.target.value)}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isActive || false}
                                            onChange={(e) => this.handleEditFormChange('isActive', e.target.checked)}
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isAdmin || false}
                                            onChange={(e) => this.handleEditFormChange('isAdmin', e.target.checked)}
                                        />
                                        <span>Admin Access</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="admin-modal-footer">
                        {modalMode === 'view' ? (
                            <>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => this.openEditModal(selectedUser)}
                                >
                                    Edit User
                                </button>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={() => this.handleDelete(selectedUser._id, selectedUser.name || selectedUser.email)}
                                >
                                    Delete User
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() => this.setState({ modalMode: 'view' })}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={this.handleSaveUser}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { users, loading, error, search, page, totalPages, totalCount } = this.state;

        return (
            <AdminWrapper>
                <div className="admin-page-header">
                    <h1 className="admin-page-title">Users</h1>
                    <p className="admin-page-subtitle">Manage all registered users</p>
                </div>

                <div className="admin-table-container">
                    <div className="admin-table-header">
                        <h3 className="admin-table-title">
                            {totalCount} {totalCount === 1 ? 'User' : 'Users'}
                        </h3>
                        <div className="admin-table-actions">
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Search by name, email, or phone..."
                                value={search}
                                onChange={this.handleSearchChange}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                            <p style={{ color: '#666' }}>Loading users...</p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
                            <button className="admin-btn admin-btn-secondary" onClick={this.fetchUsers}>
                                Retry
                            </button>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="admin-empty-state">
                            <div className="admin-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                            <h3 className="admin-empty-title">No users found</h3>
                            <p className="admin-empty-text">
                                {search ? 'Try adjusting your search criteria' : 'No users have registered yet'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user._id} onClick={() => this.props.history.push(`/beu-admin-x7k9/users/${user._id}`)} style={{ cursor: 'pointer' }}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {user.avatar ? (
                                                        <img
                                                            src={user.avatar}
                                                            alt={user.name || user.first_name}
                                                            className="admin-table-avatar"
                                                        />
                                                    ) : (
                                                        <div className="admin-table-avatar-placeholder">
                                                            {this.getInitials(user.name || `${user.first_name || ''} ${user.last_name || ''}`)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '500' }}>
                                                            {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'}
                                                        </div>
                                                        {user.isAdmin && (
                                                            <span className="admin-badge admin-badge-info" style={{ marginTop: '4px' }}>
                                                                Admin
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{user.email || 'N/A'}</td>
                                            <td>{user.phoneNumber || 'N/A'}</td>
                                            <td>
                                                <span className={`admin-badge ${user.isActive !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                                    {user.isActive !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{this.formatDate(user.createdAt || user.createdTime)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="admin-pagination">
                                    <div className="admin-pagination-info">
                                        Page {page} of {totalPages} ({totalCount} total)
                                    </div>
                                    <div className="admin-pagination-controls">
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`admin-pagination-btn ${page === pageNum ? 'active' : ''}`}
                                                    onClick={() => this.handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {this.renderModal()}
            </AdminWrapper>
        );
    }
}

export default AdminUsers;
